<template>
  <div>
    <global-confirm-box ref="confirmBox" />

    <v-row class="mb-2" dense>
      <v-col v-for="kpi in kpis" :key="kpi.label" cols="6" sm="3">
        <v-card outlined class="pa-3 fill-height">
          <div class="text-caption grey--text">{{ kpi.label }}</div>
          <div class="text-subtitle-1 font-weight-bold">{{ kpi.value }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mb-2" align="center" dense>
      <v-col cols="12" sm="5">
        <text-field
          v-model="search"
          :label="$t('finance_search_refunds_placeholder')"
          kind="search"
          clearable
          hideDetails
        />
      </v-col>
      <v-col cols="8" sm="4">
        <dropdown
          v-model="filterStatus"
          :items="statusOptions"
          itemText="label"
          itemValue="value"
          :label="$t('finance_status_label')"
          clearable
          hideDetails
        />
      </v-col>
      <v-col cols="4" sm="3" class="text-right">
        <primary-button @click="openAddDialog">
          <v-icon left small>mdi-plus</v-icon>
          {{ $t("finance_request_refund") }}
        </primary-button>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="filteredRefunds"
        :loading="loading"
        :loading-text="$t('finance_loading')"
        :no-data-text="$t('finance_no_refunds')"
        mobile-breakpoint="0"
        @click:row="openRefund"
      >
        <template #item.orderRef="{ item }">
          <span class="font-weight-bold">{{ item.orderRef }}</span>
        </template>
        <template #item.amount="{ item }">{{ formatCurrency(item.amount, restaurantCurrency) }}</template>
        <template #item.status="{ item }">
          <v-chip small :color="statusMeta(item.status).color" text-color="white">
            {{ $t(statusMeta(item.status).labelKey) }}
          </v-chip>
        </template>
      </v-data-table>
    </v-card>

    <refund-detail-dialog
      v-model="detailOpen"
      :refund="selectedRefund"
      @edit="openEditDialog"
      @delete="confirmDelete"
    />

    <refund-form-dialog
      v-model="dialogOpen"
      :saving="saving"
      :refund="editingRefund"
      @save="onSaveRefund"
    />
  </div>
</template>

<script>
import gpsApi from "@/plugins/gpsApi";
import hasSnackbar from "@/mixins/hasSnackbar";
import GlobalConfirmBox from "@/components/global/GlobalConfirmBox.vue";
import RefundDetailDialog from "./dialogs/RefundDetailDialog.vue";
import RefundFormDialog from "./dialogs/RefundFormDialog.vue";
import { REFUND_STATUS_META, formatCurrency } from "./constants";

const mapRefundDto = (dto) => ({
  id: dto.id,
  orderRef: dto.order_ref,
  customer: dto.customer,
  reason: dto.reason,
  amount: dto.amount,
  status: dto.status,
  approvedBy: dto.approved_by,
  evidence: dto.evidence,
  comment: dto.comment,
  method: dto.method,
  relatedPayout: dto.related_payout,
});

export default {
  name: "FinanceTabRefunds",

  components: { GlobalConfirmBox, RefundDetailDialog, RefundFormDialog },

  mixins: [hasSnackbar],

  data() {
    return {
      refunds: [],
      loading: false,
      saving: false,
      search: "",
      filterStatus: null,
      detailOpen: false,
      selectedRefund: null,
      dialogOpen: false,
      editingRefund: null,
    };
  },

  computed: {
    restaurantId() {
      return this.$store.state.restaurant.id;
    },
    headers() {
      return [
        { text: this.$t("finance_order"), value: "orderRef" },
        { text: this.$t("finance_customer"), value: "customer" },
        { text: this.$t("finance_reason"), value: "reason" },
        { text: this.$t("finance_amount"), value: "amount", align: "end" },
        { text: this.$t("finance_status_label"), value: "status" },
      ];
    },
    statusOptions() {
      return Object.keys(REFUND_STATUS_META).map((value) => ({
        value,
        label: this.$t(REFUND_STATUS_META[value].labelKey),
      }));
    },
    approvedRefunds() {
      return this.refunds.filter((r) => r.status === "approved");
    },
    totalRefunded() {
      return this.approvedRefunds.reduce((sum, r) => sum + Number(r.amount || 0), 0);
    },
    averageRefund() {
      return this.approvedRefunds.length > 0 ? this.totalRefunded / this.approvedRefunds.length : 0;
    },
    mostCommonReason() {
      const counts = new Map();
      this.refunds.forEach((r) => counts.set(r.reason, (counts.get(r.reason) || 0) + 1));
      let best = "";
      let bestCount = 0;
      counts.forEach((count, reason) => {
        if (count > bestCount) {
          best = reason;
          bestCount = count;
        }
      });
      return best || "—";
    },
    kpis() {
      return [
        { label: this.$t("finance_total_refunded"), value: formatCurrency(this.totalRefunded, this.restaurantCurrency) },
        { label: this.$t("finance_refund_count"), value: String(this.refunds.length) },
        { label: this.$t("finance_average_refund"), value: formatCurrency(this.averageRefund, this.restaurantCurrency) },
        { label: this.$t("finance_most_common_reason"), value: this.mostCommonReason },
      ];
    },
    filteredRefunds() {
      const query = this.search ? this.search.trim().toLowerCase() : "";
      return this.refunds.filter((refund) => {
        if (this.filterStatus && refund.status !== this.filterStatus) return false;
        if (!query) return true;
        return [refund.orderRef, refund.customer, refund.reason]
          .join(" ")
          .toLowerCase()
          .includes(query);
      });
    },
  },

  created() {
    this.refresh();
  },

  methods: {
    formatCurrency,

    statusMeta(status) {
      return REFUND_STATUS_META[status] || { labelKey: "", color: "grey" };
    },

    async refresh() {
      if (!this.restaurantId) return;
      this.loading = true;
      try {
        const { data } = await gpsApi.get(`/restaurants/${this.restaurantId}/refunds`);
        const list = (data && (data.data || data)) || [];
        this.refunds = list.map(mapRefundDto);
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_refunds_load_error"));
      } finally {
        this.loading = false;
      }
    },

    openRefund(refund) {
      this.selectedRefund = refund;
      this.detailOpen = true;
    },

    openAddDialog() {
      this.editingRefund = null;
      this.dialogOpen = true;
    },

    openEditDialog(refund) {
      this.editingRefund = refund;
      this.detailOpen = false;
      this.dialogOpen = true;
    },

    async onSaveRefund(payload) {
      this.saving = true;
      try {
        if (this.editingRefund) {
          const { data } = await gpsApi.put(`/refunds/${this.editingRefund.id}`, payload);
          const updated = mapRefundDto(data.data || data);
          this.refunds = this.refunds.map((r) => (r.id === updated.id ? updated : r));
          this.showSuccessSnackbar(this.$t("finance_refund_saved"));
        } else {
          const { data } = await gpsApi.post(`/restaurants/${this.restaurantId}/refunds`, payload);
          const created = mapRefundDto(data.data || data);
          this.refunds = [created, ...this.refunds];
          this.showSuccessSnackbar(this.$t("finance_refund_requested"));
        }
        this.dialogOpen = false;
      } catch (error) {
        this.showErrorSnackbar(
          this.editingRefund ? this.$t("finance_refund_save_error") : this.$t("finance_refund_request_error")
        );
      } finally {
        this.saving = false;
      }
    },

    confirmDelete(refund) {
      this.detailOpen = false;
      this.$refs.confirmBox
        .open(this.$t("finance_delete_refund_title"), this.$t("finance_confirm_delete_refund", { orderRef: refund.orderRef }), {
          color: "error",
        })
        .then(() => this.removeRefund(refund))
        .catch(() => {});
    },

    async removeRefund(refund) {
      try {
        await gpsApi.delete(`/refunds/${refund.id}`);
        this.refunds = this.refunds.filter((r) => r.id !== refund.id);
        this.showSuccessSnackbar(this.$t("finance_refund_deleted"));
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_refund_delete_error"));
      }
    },
  },
};
</script>
